/**
 * Google Analytics 4
 * @module
 */

var JSONEnv = require('core/lib/json');
var Logger = require('core/utils/logger');
var performanceEnv = require('core/env/performance');
var Poll = require('core/utils/poll');
var trackingPolicies = require('core/constants/tracking_policies');
var WindowEnv = require('core/env/window');

var di = require('core/di');
var RumStore = di.get('stores/rum');

var VENDOR = 'google_analytics_4';

// Global function name used by Google Analytics 4
var GAVariableName = 'gtag';
// Global variable name used by Google Tag manager
var GTMVariableName = 'dataLayer';

// Event name used by GA4 1.0
var GCustomEventName = 'optimizely_decision';

// Event name used by GA4 1.1
var GEventName = 'experience_impression';

/**
 * When a layer decision is made:
 *
 * 1. Check if decisionData has experiemntId and variationID and bail if not
 * 2. Wait for google analytics function (gtag) to become available
 * 3. Analytics function is available, call gtag with decision data
 *
 * Example:
 * ```js
 *  gtag("event", "optimizely_decision", {
 *     Experiment: experimentObject,
 *     Variation: variationObject
 *   });
 * ```
 *
 * @param {DecisionData} decisionData
 * @return {Promise} Resolves when tracked with GA4 API, or error occurs (in which case the error is logged)
 */
var trackLayerDecisionGA4 = function(decisionData:DecisionData): Promise<any> {
  var settings = decisionData.integrationSettings;
  if (!settings || !settings[VENDOR]) {
    Logger.debug(`GA4 Integration: did not find vendor ${VENDOR} in integration settings, exiting`);
    return;
  }
  if (!decisionData.experimentId || !decisionData.variationId) {
    Logger.debug('GA4 Integration: empty experiment or variation ID, exiting');
    return;
  }

  const isGTM = settings[VENDOR].is_using_gtm === 'on';
  Logger.debug(`GA4 Integration: is using GTM: ${isGTM}`);

  const isPersonalizationCampaign = decisionData.layerPolicy === 'equal_priority';
  Logger.debug(`GA4 Integration: is personalization campaign: ${isPersonalizationCampaign}`);

  let firstPartyData;
  let customEventData;

  if (isPersonalizationCampaign){
    firstPartyData = `OPT-${decisionData.layerId}(${decisionData.layerName})-${decisionData.experimentId}(${decisionData.experimentName})-${decisionData.variationId}(${decisionData.variationName})`;
    customEventData = {
      Campaign: decisionData.layerName + ' (' + decisionData.layerId + ')',
      Variation: decisionData.variationName + ' (' + decisionData.variationId + ')',
      Holdback: decisionData.isLayerHoldback,
    };
  }else{
    firstPartyData = `OPT-${decisionData.experimentId}(${decisionData.experimentName})-${decisionData.variationId}(${decisionData.variationName})`;
    customEventData = {
      Experiment: decisionData.experimentName + ' (' + decisionData.experimentId + ')',
      Variation: decisionData.variationName + ' (' + decisionData.variationId + ')',
      Holdback: decisionData.isLayerHoldback,
    };
  }

  const data = {
    exp_variant_string: firstPartyData,
    Holdback: decisionData.isLayerHoldback,
  };

  if (isGTM){
    return trackDecisionWithGTM(data, customEventData)['catch'](function(e) {
      Logger.warn('Tracker for GTM ', VENDOR, 'failed:', e);
    });
  }else{
    return trackDecisionWithGA4(data, customEventData)['catch'](function(e) {
      Logger.warn('Tracker for ', VENDOR, 'failed:', e);
    });
  }
};


var trackDecisionWithGA4 = function(data, customEventData) {
  const shouldAlsoSendCustomEvent = __GA4_CUSTOM_FIELD__;

  var MAX_RETRIES = 50,
      WAIT_TIME = __TEST__ ? 5 : 200;

  return Poll.pollFor(function() {
    return getGA4Object(WindowEnv);
  }, WAIT_TIME, MAX_RETRIES)
  .then(function(gaFunction) {
    Logger.log(`GA4 Integration: ${GAVariableName} finally exists, sending now.`);
    (__PROFILE__ || __RUM_VERBOSE__ || RumStore.getSampleRum()) && performanceEnv.timeEnd('gtagReady');
    Logger.debug(`GA4 Integration: calling ga4 with data: ${JSONEnv.stringify(data)}`);
    gaFunction('event', GEventName, data);

    if(shouldAlsoSendCustomEvent){
      Logger.debug(`GA4 Integration: calling ga4 with custom event data: ${JSONEnv.stringify(customEventData)}`);
      gaFunction('event', GCustomEventName, customEventData);
    }

  });
};

function getGA4Object(window): Function|null {
  var GA4ObjectName = window.getGlobal(GAVariableName);
  if (!GA4ObjectName) {
    return null;
  }
  return GA4ObjectName;
}

var trackDecisionWithGTM = function(data, customEventData) {
  const shouldAlsoSendCustomEvent = __GA4_CUSTOM_FIELD__;

  var dataLayerObj = WindowEnv.getGlobal(GTMVariableName);
  Logger.debug(`GA4 Integration: found dataLayer object ${dataLayerObj}`);
  if (!dataLayerObj){
    Logger.debug(`GA4 Integration: dataLayer object ${GTMVariableName} is empty, initializing it`);
    WindowEnv.setGlobal(GTMVariableName, []);
    dataLayerObj = WindowEnv.getGlobal(GTMVariableName);
  }
  Logger.log(`GA4 Integration: attempting push to ${GTMVariableName}`);
  try{
    let gtmData = Object.assign({'event': GEventName}, data); // eslint-disable-line lodash/prefer-lodash-method
    Logger.debug(`GA4 Integration: pushing data to GTM: ${JSONEnv.stringify(gtmData)}`);
    dataLayerObj.push(gtmData);

    if(shouldAlsoSendCustomEvent){
      let customGtmData = Object.assign({'event': GCustomEventName}, customEventData); // eslint-disable-line lodash/prefer-lodash-method
      Logger.debug(`GA4 Integration: pushing custom data to GTM: ${JSONEnv.stringify(customGtmData)}`);
      dataLayerObj.push(customGtmData);
    }

  }catch(e){
    Logger.warn(`GA4 Integration: Push to ${GTMVariableName} failed: ${e}`);
  }
  return Promise.resolve();

};

var trackerGA4 = {
  preRedirectPolicy: trackingPolicies.PreRedirectPolicies.PERSIST_BEFORE_REDIRECT,
  postRedirectPolicy: trackingPolicies.PostRedirectPolicies.TRACK_IMMEDIATELY,
  nonRedirectPolicy: trackingPolicies.NonRedirectPolicies.TRACK_IMMEDIATELY,
  trackLayerDecision: trackLayerDecisionGA4,
  serializeSettings: JSONEnv.stringify,
  deserializeSettings: JSONEnv.parse,
};

module.exports = function(PluginManager) {
  PluginManager.registerAnalyticsTracker('google_analytics_4', trackerGA4);
};

if (__TEST__) {
  module.exports.plugin = trackerGA4;
}
